"use client";
import { Button } from "@fanduel/formation-react-components";
import {
  ArrowsChevronDown,
  ArrowsChevronUp,
} from "@fanduel/formation-react-icons";
import { fanduel as fd } from "@fanduel/formation-tokens";
import { useQuery } from "@tanstack/react-query";
import { groupBy } from "lodash-es";
import { useState } from "react";
import TeamContainer from "@/components/headToHead/TeamContainer";
import {
  HEAD_TO_HEAD_INJURIES_EXPANDED,
  HEAD_TO_HEAD_STATUS_MAP,
} from "@/constants/amplitude";
import { TIME_IN_MS } from "@/constants/time";
import { GetGameInjuriesQuery } from "@/graphql/gql/graphql";
import { getGameInjuries } from "@/utils/api";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import { useHeadToHeadContext } from "@/utils/hooks/useHeadToHeadContext";
import { filterEmpty } from "@/utils/index";
import { InjuryBadge, PlayerDetails } from "./InjuryBadge";
import styles from "./Injuries.module.scss";

interface InjuryDisplayProps {
  injuriesData: GetGameInjuriesQuery;
  eventId: number;
  isLive: boolean | undefined;
}

export default function InjuryDisplay({
  injuriesData,
  eventId,
  isLive,
}: InjuryDisplayProps) {
  const { homeTeam, awayTeam, eventName, gameState } = useHeadToHeadContext();
  const [showAll, setShowAll] = useState(false);
  const { data: injuriesByTeam } = useQuery({
    queryKey: ["injuries", eventId],
    queryFn: () => getGameInjuries(eventId),
    select: (data: GetGameInjuriesQuery) =>
      groupBy(data.event?.playerInjuries, "teamInfo.name"),
    initialData: injuriesData,
    enabled: !!isLive,
    refetchInterval: isLive ? TIME_IN_MS.SEC_30 : undefined,
  });
  const homeTeamName = homeTeam?.name ?? "";
  const awayTeamName = awayTeam?.name ?? "";
  const homeTeamInjuries = injuriesByTeam[homeTeamName] ?? [];
  const awayTeamInjuries = injuriesByTeam[awayTeamName] ?? [];
  const homeColor = homeTeam?.primaryColor ?? fd.colors.contentStrong;
  const awayColor = awayTeam?.primaryColor ?? fd.colors.contentStrong;
  const homeIcon = homeTeam?.logoSVG ?? "";
  const awayIcon = awayTeam?.logoSVG ?? "";

  const homeInjuries = filterEmpty(homeTeamInjuries ?? []);
  const awayInjuries = filterEmpty(awayTeamInjuries ?? []);

  const { logAmplitudeEvent } = useAmplitude();
  const toggleInjuriesExpansion = () => {
    if (!showAll) {
      logAmplitudeEvent(HEAD_TO_HEAD_INJURIES_EXPANDED, {
        "Event Name": eventName,
        Module: "injuries",
        "Page State": HEAD_TO_HEAD_STATUS_MAP[gameState],
      });
    }
    setShowAll((val) => !val);
  };

  return (
    <div className={styles.injuriesContainer}>
      <div className={styles.teamsContainer}>
        <TeamContainer
          teamName={awayTeamName}
          teamColor={awayColor}
          teamIcon={awayIcon}
          showColumHeaders
        >
          {awayInjuries.slice(0, showAll ? undefined : 6).map((player) => (
            <div className={styles.playerListItem} key={player.name}>
              <PlayerDetails
                playerName={player.name}
                playerPosition={player.position}
                jerseyUrl={player.teamInfo?.teamJersey?.svg}
                ampModule="injuries"
              />
              <InjuryBadge player={player} />
            </div>
          ))}
        </TeamContainer>
        <TeamContainer
          teamColor={homeColor}
          teamName={homeTeamName}
          teamIcon={homeIcon}
          showColumHeaders
        >
          {homeInjuries.slice(0, showAll ? undefined : 6).map((player) => (
            <div className={styles.playerListItem} key={player.name}>
              <PlayerDetails
                playerName={player.name}
                playerPosition={player.position}
                jerseyUrl={player.teamInfo?.teamJersey?.svg}
                ampModule="injuries"
              />
              <InjuryBadge player={player} />
            </div>
          ))}
        </TeamContainer>
      </div>
      {homeInjuries.length > 6 || awayInjuries.length > 6 ? (
        <Button
          label={`Show ${showAll ? "Less" : "All"} Injury Updates`}
          variant="tertiary"
          iconRight={showAll ? <ArrowsChevronUp /> : <ArrowsChevronDown />}
          onClick={toggleInjuriesExpansion}
        />
      ) : null}
    </div>
  );
}
